import React from 'react';
import clsx from 'clsx';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useHistory, useParams } from 'react-router-dom';
import {
  Drawer,
  AppBar,
  Toolbar,
  useMediaQuery,
  Box,
  InputBase,
  Tooltip,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
} from '@material-ui/core';

import DynamicIcon from 'components/Common/DynamicIcon';
import { ChevronRight, ExitToApp, ExpandMore } from '@material-ui/icons';
import { sendAmplitudeData } from 'util/amplitude';
import { configure, GlobalHotKeys } from 'react-hotkeys';
import { deeplinkToURL } from 'services/deeplink';
import { preventDefaultHandlers } from 'util/misc';

const drawerWidth = 280;

export const useStyles = makeStyles((theme) => ({
  appBar: {
    width: `calc(100% - ${theme.spacing(10)}px)`,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    boxShadow: '0px 2.5px 20px #0000002d',
  },
  // appBarOpen: {
  //   width:`calc(100% - ${drawerWidth}px)`,
  //   [theme.breakpoints.down('sm')]: {
  //     zIndex: theme.zIndex.modal + 1,
  //   },
  //   transition: theme.transitions.create('width', {
  //     easing: theme.transitions.easing.sharp,
  //     duration: theme.transitions.duration.enteringScreen,
  //   }),
  // },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  drawerOpen: {
    backgroundColor: '#f9f9f9',
    overflowX: 'hidden',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    display: 'flex',
    flexDirection: 'column',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: theme.spacing(10),
    borderRightWidth: 0,
    boxShadow: '0px 2.5px 20px #0000002d',
  },
  // chip: {
  //   minWidth: 24,
  //   maxWidth: '100%',
  // },
  logo: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  topToolBar: {
    padding: theme.spacing(0, 4),
    justifyContent: 'space-between',
    flexShrink: 1,
  },
  search: {
    display: 'flex',
    borderRadius: 5,
    backgroundColor: '#F7F7F7',
    '&:hover': {
      backgroundColor: '#efefef',
    },
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1),
      width: 'auto',
    },
    marginRight: theme.spacing(3),
  },
  searchIcon: {
    background: 'linear-gradient(240deg, #7467F0 0%, #2896E9 100%)',
    padding: theme.spacing(0, 2.5),
    borderRadius: 5,
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputRoot: {
    color: 'inherit',
  },
  inputInput: {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(${theme.spacing(3)}px)`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '20ch',
      '&:focus': {
        width: '25ch',
      },
    },
  },
}));

// function MobileDrawer({open, onClose, children}) {
//   const classes = useStyles();
//   return <Drawer open={open} onClose={onClose} variant={"temporary"}
//       className={classes.drawer} classes={{ paper: classes.drawer }}
//     >
//       {children}
//     </Drawer>;
// }

function DesktopDrawer({ open, setOpen, onClose, children }) {
  const classes = useStyles();
  return (
    <Drawer
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      open={open}
      onClose={onClose}
      variant="permanent"
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: open,
        [classes.drawerClose]: !open,
      })}
      classes={{
        paper: clsx({
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        }),
      }}
    >
      {children}
    </Drawer>
  );
}

function ListMenu({ menu, onLinkClick, open, selected, setSelected }) {
  const handleSelected = (href) => {
    setSelected(href);
  };
  const [expandedListIndices, setExpandedListIndices] = React.useState([]);

  const expandList = (index) => {
    expandedListIndices.includes(index)
      ? setExpandedListIndices(
          expandedListIndices.filter((itr) => itr !== index)
        )
      : setExpandedListIndices([...expandedListIndices, index]);
  };
  React.useEffect(() => {
    !!window &&
      !!window.location &&
      setSelected(`/reset${window.location.pathname}`);
  }, []);

  return (
    <List style={{ width: '100%' }}>
      {menu.items &&
        menu.items.map((link, index) => (
          <>
            <ListItem
              button
              key={link.id}
              onClick={() => {
                !!link.children && expandList(index);
                !link.children && onLinkClick(link);
                !link.children && handleSelected(link.href);
              }}
            >
              <ListItemIcon>
                <Box p={1.5}>
                  <DynamicIcon
                    iconId={
                      selected === link.href ||
                      (link.children &&
                        !!link.children.find((item) => item.href === selected))
                        ? link.iconId.selected
                        : link.iconId.notSelected
                    }
                  />
                </Box>
              </ListItemIcon>
              {open && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'space-between',
                  }}
                >
                  <ListItemText
                    style={{
                      color:
                        (selected === link.href ||
                          (link.children &&
                            !!link.children.find(
                              (item) => item.href === selected
                            ))) &&
                        '#7467f0',
                    }}
                    primary={link.title}
                    secondary={link.subtitle}
                    secondaryTypographyProps={{
                      style: {
                        color:
                          (selected === link.href ||
                            (link.children &&
                              !!link.children.find(
                                (item) => item.href === selected
                              ))) &&
                          '#7467f0',
                      },
                    }}
                  />
                  {!!link.children && (
                    <ListItemIcon>
                      {expandedListIndices.includes(index) ? (
                        <ChevronRight
                          style={{
                            color:
                              !!link.children.find(
                                (item) => item.href === selected
                              ) && '#7467f0',
                          }}
                        />
                      ) : (
                        <ExpandMore
                          style={{
                            color:
                              !!link.children.find(
                                (item) => item.href === selected
                              ) && '#7467f0',
                          }}
                        />
                      )}
                    </ListItemIcon>
                  )}
                </div>
              )}
            </ListItem>
            {link.children && expandedListIndices.includes(index) && open && (
              <ListItem style={{ background: '#ffffff' }}>
                <List style={{ width: '100%' }}>
                  {link.children.map((item) => (
                    <ListItem
                      style={{ width: '100%' }}
                      button
                      key={link.id}
                      onClick={() => {
                        onLinkClick(item);
                        handleSelected(item.href);
                      }}
                    >
                      <ListItemIcon>
                        <Box pt={0.5} pb={0.5} pl={1.5} pr={0.5}>
                          <DynamicIcon
                            iconId={
                              item.href === selected
                                ? item.iconId.selected
                                : item.iconId.notSelected
                            }
                          />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        style={{
                          color: selected === item.href && '#7467f0',
                        }}
                        primary={item.title}
                        secondary={item.subtitle}
                        secondaryTypographyProps={{
                          style: {
                            color: selected === item.href && '#7467f0',
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </ListItem>
            )}
          </>
        ))}
    </List>
  );
}

export function MenuDrawer({
  menuLinks,
  toolBarComponent,
  search,
  refreshScreenHandler,
}) {
  const [selected, setSelected] = React.useState();
  const [shortcutKeyMap, setShortcutKeyMap] = React.useState({});
  const [shortcutHandlers, setShortcutHandlers] = React.useState({});
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = React.useState(false);
  const toggleDrawerOpen = () => {
    setOpen(!open);
  };
  const { target } = useParams();
  const onLinkClick = (link) => {
    sendAmplitudeData('SideBar clicked', { link: link.href });
    isMobile && setOpen(false);
    if (link.href.replace('/reset', '') === window.location.pathname) {
      (refreshScreenHandler.current.refresher || (() => {}))(
        link.href.replace('/reset/page', '')
      );
    } else {
      link.href && history.push(link.href);
    }
  };
  React.useEffect(() => {
    setOpen(false);
  }, [isMobile]);
  configure({
    ignoreTags: ['input', 'select', 'textarea'],
    ignoreEventsCondition: function () {},
  });
  React.useEffect(() => {
    if (!!menuLinks) {
      let tempShortcutKeyMap = {};
      let tempShortcutHandlers = {};
      (menuLinks[0].shortcuts || []).forEach((shortcut) => {
        tempShortcutKeyMap[shortcut.name] = [...shortcut.keys];
        tempShortcutHandlers[shortcut.name] = () => {
          onLinkClick({
            href: deeplinkToURL(shortcut.deeplink, '/reset/page'),
          });
          setSelected(deeplinkToURL(shortcut.deeplink, '/reset/page'));
        };
      });
      setShortcutKeyMap({ ...tempShortcutKeyMap });
      setShortcutHandlers(preventDefaultHandlers({ ...tempShortcutHandlers }));
    }
  }, [menuLinks]);

  const DrawerComponent = DesktopDrawer;

  return (
    <>
      {((!!toolBarComponent && toolBarComponent.length > 0) || !!search) && (
        <AppBar
          position="fixed"
          className={clsx(classes.appBar, open && classes.appBarOpen)}
        >
          <Toolbar className={classes.topToolBar}>
            {toolBarComponent}
            {search && (
              <Box className={classes.search}>
                <InputBase
                  placeholder="Search…"
                  classes={{
                    root: classes.inputRoot,
                    input: classes.inputInput,
                  }}
                  inputProps={{ 'aria-label': 'search' }}
                />
                <Box p={0.5} display="flex" style={{ cursor: 'pointer' }}>
                  <Box className={classes.searchIcon}>
                    <DynamicIcon icon="search" />
                  </Box>
                </Box>
              </Box>
            )}
          </Toolbar>
        </AppBar>
      )}
      {target !== 'magicform' && !isMobile && target !== 'magicscreen' && (
        <DrawerComponent
          open={open}
          setOpen={setOpen}
          onClose={toggleDrawerOpen}
        >
          <GlobalHotKeys
            keyMap={shortcutKeyMap}
            handlers={shortcutHandlers}
            allowChanges={true}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <Box className={classes.logo} style={{ width: 60 }}>
              {menuLinks && menuLinks[0].logo && (
                <img src={menuLinks[0].logo} alt="logo" width={60} />
              )}
            </Box>
            {open && (
              <Box
                style={{
                  width: 160,
                  display: 'flex',
                  justifyContent: 'flex-start',
                  marginLeft: 10,
                }}
                className={classes.logo}
              >
                <Typography
                  style={{
                    maxWidth: 160,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordWrap: 'nowrap',
                    whiteSpace: 'nowrap',
                    fontWeight: 'bold',
                  }}
                >
                  {(menuLinks && menuLinks[0].title) || 'Pharmacy One'}
                </Typography>
              </Box>
            )}
          </div>
          <Divider variant="middle" />
          <Box
            pt={1}
            display="flex"
            flexDirection="column"
            alignItems="center"
            flex={1}
            overflow="auto"
          >
            {menuLinks &&
              menuLinks.map((menu, index) => (
                <ListMenu
                  selected={selected}
                  setSelected={setSelected}
                  key={index}
                  menu={menu}
                  onLinkClick={onLinkClick}
                  open={open}
                />
              ))}
          </Box>
          <Divider variant="middle" />
          <Box display="flex" pt={2} pb={2} justifyContent="center">
            <Tooltip title={'Logout'} enterDelay={500} enterNextDelay={500}>
              <Button
                disableRipple
                onClick={() => history.push('/logout')}
                style={{ width: '100%' }}
              >
                <Box className={classes.logo} style={{ width: 60 }}>
                  <ExitToApp style={{ fontSize: 30, color: '#898989' }} />
                </Box>
                {open && (
                  <Box
                    style={{
                      width: 140,
                      display: 'flex',
                      justifyContent: 'flex-start',
                      marginLeft: 10,
                    }}
                    className={classes.logo}
                  >
                    <Typography style={{ maxWidth: 140 }}>Log Out</Typography>
                  </Box>
                )}
              </Button>
            </Tooltip>
          </Box>
        </DrawerComponent>
      )}
    </>
  );
}
